import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../../components/ui/Icon";
import { ApiError } from "../../lib/api";
import { useNative } from "../../../shared/phone/media";
import { AdPlayer } from "./AdPlayer";
import { WORDS_A_SECOND } from "./ScriptLines";
import { useRecorder } from "./useRecorder";

const count = (line: string) => line.split(/\s+/).filter(Boolean).length;
const RATE_KEY = "flashadz-read-rate";
// Why: people reading to a camera go a little slower than they talk; Slower and Faster change it, and it is kept.
const FIRST_RATE = 0.85;
function keptRate(): number {
  try {
    const r = Number(localStorage.getItem(RATE_KEY));
    return r >= 0.5 && r <= 2 ? r : FIRST_RATE;
  } catch {
    return FIRST_RATE;
  }
}

/**
 * The script full screen. The words wait at the top until Record (or Start, without the camera), then roll at
 * speaking pace, each line passing the band for as long as it takes to say. Space or a tap on the words
 * pauses. A take the owner keeps becomes the ad's video, or its Stories and Reels version.
 */
export function ScriptReader({
  lines,
  canRecord,
  startCamera,
  slotFor,
  onUse,
  onClose,
}: {
  lines: string[];
  canRecord: boolean;
  /** Turn the camera on as the reader opens: it was opened to record. */
  startCamera?: boolean;
  /** Where a take of this shape goes: the ad's video, the Stories and Reels version, or nowhere. */
  slotFor: (wide: boolean) => "main" | "tall" | null;
  onUse: (file: File, slot: "main" | "tall") => Promise<void>;
  onClose: () => void;
}) {
  const rec = useRecorder();
  const native = useNative();
  const [rate, setRate] = useState(keptRate);
  const [paused, setPaused] = useState(true);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const dialog = useRef<HTMLDivElement>(null);
  const text = useRef<HTMLDivElement>(null);
  const spoken = useRef(0);
  const camera = rec.stream !== null;
  const touch = matchMedia("(pointer: coarse)").matches;
  // Why: with the camera on the words wait at the top until recording starts, so the take begins with line 1.
  const rolling = !paused && (!camera || rec.state === "recording");

  // Why: focus starts in the reader, so Space pauses it instead of pressing the button that opened it.
  useEffect(() => dialog.current?.focus(), []);
  const refocus = () => dialog.current?.focus();
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, as the reader opens.
  useEffect(() => {
    if (startCamera && canRecord) void rec.open();
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(RATE_KEY, String(rate));
    } catch {
      // Why: a browser that keeps nothing still reads at the chosen pace for this visit.
    }
  }, [rate]);

  const live = useCallback(
    (el: HTMLVideoElement | null) => {
      if (el && rec.stream && el.srcObject !== rec.stream) el.srcObject = rec.stream;
    },
    [rec.stream],
  );

  useEffect(() => {
    const el = text.current;
    if (!rolling || !el || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ps = [...el.children] as HTMLElement[];
    if (!ps.length) return;
    const tops = ps.map((p) => p.offsetTop - ps[0].offsetTop);
    const ends = ps.map((p, i) => (i + 1 < ps.length ? tops[i + 1] : tops[i] + p.offsetHeight));
    const words = lines.map(count);
    const total = words.reduce((a, b) => a + b, 0);
    let raf = 0;
    let last = 0;
    const tick = (t: number) => {
      spoken.current = Math.min(total, spoken.current + (last ? (t - last) / 1000 : 0) * WORDS_A_SECOND * rate);
      last = t;
      let w = spoken.current;
      let i = 0;
      while (i < words.length - 1 && w >= words[i]) {
        w -= words[i];
        i += 1;
      }
      const y = tops[i] + Math.min(1, words[i] ? w / words[i] : 1) * (ends[i] - tops[i]);
      el.style.transform = `translateY(${-y}px)`;
      ps.forEach((p, k) => p.classList.toggle("on", k === i));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [rolling, rate, lines]);

  useEffect(() => {
    if (rec.state !== "recording") return;
    const from = Date.now();
    setElapsed(0);
    const id = setInterval(() => setElapsed(Math.round((Date.now() - from) / 1000)), 500);
    return () => clearInterval(id);
  }, [rec.state]);

  const slot = rec.take ? slotFor(rec.take.wide) : null;
  const use = async () => {
    if (!rec.take || !slot) return;
    setSaving(true);
    setProblem(null);
    try {
      await onUse(rec.take.file, slot);
      onClose();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : "The take did not save. Try again.");
    } finally {
      setSaving(false);
    }
  };
  // Why: a finished take is kept unless the owner records again; Done never drops it.
  const done = () => (rec.take && slot ? void use() : onClose());

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape" && rec.state !== "recording" && !saving) onClose();
      if (e.code === "Space" && !rec.take && !(e.target instanceof HTMLButtonElement)) {
        e.preventDefault();
        setPaused((p) => !p);
      }
    };
    addEventListener("keydown", key);
    return () => removeEventListener("keydown", key);
  }, [onClose, rec.state, rec.take, saving]);

  const record = () => {
    spoken.current = 0;
    if (text.current) text.current.style.transform = "translateY(0)";
    setPaused(false);
    rec.start();
  };
  const pace = (by: number) => {
    setRate((r) => Math.round(Math.min(2, Math.max(0.5, r + by)) * 100) / 100);
    refocus();
  };
  const hint =
    rec.state === "recording"
      ? `Recording · ${elapsed}s of 15 to 30`
      : camera
        ? "The words roll when you press Record."
        : touch
          ? "Tap the words to pause."
          : "Space pauses.";

  const err = rec.error ?? problem;
  // Why: on a phone and an iPad the reader is drawn inside the app, so the app's own styles reach it.
  if (native)
    return createPortal(
      <div className={camera ? "rdr ad-rdr cam" : "rdr ad-rdr"} role="dialog" aria-modal="true" aria-label="Read it" tabIndex={-1} ref={dialog}>
        {camera && !rec.take && <video className="rdr-cam live" ref={live} autoPlay muted playsInline aria-label="Your camera" />}
        {rec.take && (
          <div className="ad-rtake">
            <AdPlayer src={rec.take.url} label="Your take" fill />
          </div>
        )}
        <div className="ad-rbar">
          <button className="ad-rb" type="button" disabled={rec.state === "recording" || saving} onClick={done}>
            {saving ? "Saving" : rec.take && !slot ? "Close" : "Done"}
          </button>
          {!rec.take && (
            <span className="ad-rsp">
              <button className="ad-rb" type="button" onClick={() => pace(-0.1)}>
                Slower
              </button>
              <span className="ad-rate">{Number(rate.toFixed(2))}×</span>
              <button className="ad-rb" type="button" onClick={() => pace(0.1)}>
                Faster
              </button>
            </span>
          )}
        </div>
        <p className="ad-rhint">
          {rec.state === "recording" && <span className="ad-rdot" aria-hidden="true" />}
          <span>{rec.take ? `Your take, ${Math.round(rec.take.seconds)} seconds` : hint}</span>
        </p>
        {err && <p className="ad-rerr">{err}</p>}
        {!canRecord && !!navigator.mediaDevices?.getUserMedia && !err && <p className="ad-rerr">To record here, open Flashadz in Safari or Chrome.</p>}
        {!rec.take && (
          // biome-ignore lint/a11y/useKeyWithClickEvents: Space does the same from the keyboard.
          <div className="ad-rwin" onClick={() => !camera && setPaused((p) => !p)}>
            <div className="ad-band" aria-hidden="true" />
            <div className="rdr-text ad-rl fs-norecord" ref={text}>
              {lines.map((l, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: lines are read in order and never move while reading.
                <p key={i} className={i === 0 ? "on" : undefined}>
                  {l}
                </p>
              ))}
            </div>
          </div>
        )}
        <div className="ad-rfoot">
          {rec.take ? (
            <>
              {slot && rec.take.wide && <span className="ad-rnote">This take is wide. For Reels and Stories, record with your phone held upright.</span>}
              {!slot && <span className="ad-rnote">This take is wide, so it cannot be the upright version. Hold your phone upright and record again.</span>}
              <div className="ad-rdone">
                {slot && (
                  <button className="pn-btn pri" type="button" disabled={saving} onClick={() => void use()}>
                    {slot === "tall" ? "Use for Stories and Reels" : "Use this take"}
                  </button>
                )}
                <button className="pn-btn ad-ragain" type="button" disabled={saving} onClick={rec.again}>
                  Record again
                </button>
              </div>
            </>
          ) : camera ? (
            <>
              <button
                className={rec.state === "recording" ? "ad-rec on" : "ad-rec"}
                type="button"
                disabled={rec.state !== "ready" && rec.state !== "recording"}
                aria-label={rec.state === "recording" ? "Stop" : "Record"}
                onClick={() => {
                  if (rec.state === "recording") rec.stop();
                  else record();
                  refocus();
                }}
              >
                <i />
              </button>
              <span className="ad-recl">{rec.state === "recording" ? "Stop" : "Record"}</span>
            </>
          ) : (
            <div className="ad-rdone">
              <button
                className="pn-btn ad-ragain"
                type="button"
                onClick={() => {
                  setPaused((p) => !p);
                  refocus();
                }}
              >
                <Icon name={paused ? "play" : "pause"} />
                {paused ? "Start" : "Pause"}
              </button>
              {canRecord && (
                <button className="pn-btn pri" type="button" disabled={rec.state === "starting"} onClick={() => void rec.open()}>
                  <Icon name="adCam" />
                  Record
                </button>
              )}
            </div>
          )}
        </div>
      </div>,
      document.querySelector(".pn-app") ?? document.body,
    );

  return createPortal(
    <div className={camera ? "ga rdr cam" : "ga rdr"} role="dialog" aria-modal="true" aria-label="Read it" tabIndex={-1} ref={dialog}>
      <div className="rdr-bar">
        <span className="rdr-hint">{hint}</span>
        <span className="rdr-acts">
          {canRecord && !camera && (
            <button className="btn sm" type="button" disabled={rec.state === "starting"} onClick={() => void rec.open()}>
              <Icon name="film" />
              Record with your camera
            </button>
          )}
          {!camera && (
            <button
              className="btn sm ghost"
              type="button"
              onClick={() => {
                setPaused((p) => !p);
                refocus();
              }}
            >
              <Icon name={paused ? "play" : "pause"} />
              {paused ? "Start" : "Pause"}
            </button>
          )}
          <button className="btn sm ghost" type="button" onClick={() => pace(-0.1)}>
            Slower
          </button>
          <button className="btn sm ghost" type="button" onClick={() => pace(0.1)}>
            Faster
          </button>
          <button className="btn sm" type="button" disabled={rec.state === "recording" || saving} onClick={done}>
            {saving && <span className="spin" />}
            {rec.take && !slot ? "Close" : "Done"}
          </button>
        </span>
      </div>
      {(rec.error ?? problem) && <p className="rdr-err">{rec.error ?? problem}</p>}
      {!canRecord && !!navigator.mediaDevices?.getUserMedia && <p className="rdr-note top">To record here, open Flashadz in Chrome or Safari.</p>}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Space does the same from the keyboard. */}
      <div className="rdr-view" onClick={() => !rec.take && !camera && setPaused((p) => !p)}>
        {camera && !rec.take && <video className="rdr-cam live" ref={live} autoPlay muted playsInline aria-label="Your camera" />}
        {rec.take ? (
          <AdPlayer src={rec.take.url} label="Your take" fill />
        ) : (
          <div className="rdr-text fs-norecord" ref={text}>
            {lines.map((l, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: lines are read in order and never move while reading.
              <p key={i}>{l}</p>
            ))}
          </div>
        )}
      </div>
      {camera && (
        <div className="rdr-rec">
          {rec.state === "ready" && (
            <button
              className="rec"
              type="button"
              onClick={() => {
                record();
                refocus();
              }}
            >
              <i />
              Record
            </button>
          )}
          {rec.state === "recording" && (
            <button
              className="rec on"
              type="button"
              onClick={() => {
                rec.stop();
                refocus();
              }}
            >
              <i />
              Stop
            </button>
          )}
          {rec.state === "done" && rec.take && (
            <>
              {slot && (
                <button className="btn" type="button" disabled={saving} onClick={() => void use()}>
                  {saving && <span className="spin" />}
                  {slot === "tall" ? "Use for Stories and Reels" : "Use this take"}
                </button>
              )}
              <button className="btn ghost" type="button" disabled={saving} onClick={rec.again}>
                Record again
              </button>
              {!slot ? (
                <span className="rdr-note">This take is wide, so it cannot be the upright version. Hold your phone upright and record again.</span>
              ) : (
                rec.take.wide && <span className="rdr-note">This take is wide. For Reels and Stories, record on your phone held upright.</span>
              )}
            </>
          )}
        </div>
      )}
    </div>,
    document.body,
  );
}
